import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClimbRecord } from '../models/user/Climber';
import { ClimbingRoute } from '../models/climbing/ClimbingRoute';
import { ClimbPerformance, getGradeAsString, GradeSystem } from '../models/climbing/Grade';
import { AvailableColors } from '../models/climbing/RouteColors';
import { getClimber } from '../services/firebase/climber';
import { updateClimb, deleteClimb } from '../services/firebase/climbs';
import { getGym } from '../services/firebase/gym';
import { getClimbingRoutes } from '../services/firebase/climbingRoute';
import { Gym } from '../models/gym/Gym';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ClimbDetail.css';
import '../styles/RankingsList.css';
import PageHeader from '../components/layout/PageHeader';
import MyIconButton from '../components/ui/MyIconButton';

const ClimbDetail: React.FC = () => {
    const { gymId, climbIndex } = useParams<{ gymId: string; climbIndex: string }>();
    const navigate = useNavigate();
    const { currentUser, climberProfile } = useAuth();
    
    const [climb, setClimb] = useState<ClimbRecord | null>(null);
    const [route, setRoute] = useState<ClimbingRoute | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    
    // Edit form state
    const [editPerformance, setEditPerformance] = useState<ClimbPerformance>(ClimbPerformance.TOPPED);
    const [isTopped, setIsTopped] = useState<boolean>(true);
    const [editHangs, setEditHangs] = useState<number>(0);
    const [editAttempt, setEditAttempt] = useState<number>(1);
    const [editNotes, setEditNotes] = useState<string>('');
    const [editStyle, setEditStyle] = useState<string>('');
    const [editReachedTop, setEditReachedTop] = useState<boolean>(true);
    const [editClimbDate, setEditClimbDate] = useState<string>('');
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        const fetchClimbData = async () => {
            if (!currentUser || !gymId || climbIndex === undefined) {
                setError('Missing required parameters');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');

                // Get climber data to access climbs array
                const climber = await getClimber(currentUser.uid);
                if (!climber || !climber.climbs) {
                    setError('No climbs found');
                    setLoading(false);
                    return;
                }

                const climbIdx = parseInt(climbIndex);
                if (isNaN(climbIdx) || climbIdx < 0 || climbIdx >= climber.climbs.length) {
                    setError('Invalid climb index');
                    setLoading(false);
                    return;
                }

                const climbData = climber.climbs[climbIdx];
                setClimb(climbData);

                // Initialize edit form with current values
                const currentIsTopped = climbData.performance !== ClimbPerformance.NOT_TOPPED;
                setEditPerformance(climbData.performance);
                setIsTopped(currentIsTopped);
                setEditHangs(climbData.hangs || 0);
                setEditAttempt(climbData.attempt || 1);
                setEditNotes(climbData.notes || '');
                setEditStyle(climbData.style || '');
                setEditReachedTop(climbData.reachedTop !== undefined ? climbData.reachedTop : true);
                setEditClimbDate(new Date(climbData.date).toISOString().split('T')[0]);

                // Get gym data
                const gymData = await getGym(gymId);
                if (gymData) {
                    
                    // Get routes data
                    const routesData = await getClimbingRoutes(gymId);
                    
                    // Find the route in the routes data
                    const routeData = routesData.find((r: ClimbingRoute) => r.id === climbData.routeId);
                    if (routeData) {
                        setRoute(routeData);
                    } else {
                        // Route not found in active routes (possibly archived)
                        // Create a route object from embedded climb data
                        console.warn('Route not found in active routes, using embedded climb data');
                        const embeddedRoute = new ClimbingRoute(climbData.routeId, {
                            grade: climbData.grade,
                            color: climbData.color || '',
                            rope: climbData.rope || '',
                            setterName: climbData.setterName || 'Unknown setter',
                            setterId: climbData.setterId || '',
                            status: climbData.status || 'archived',
                            date: climbData.routeDate || climbData.date,
                            location: climbData.location,
                            points: climbData.points || 1,
                            note: climbData.routeNote,
                            comments: climbData.routeComments,
                            picture: climbData.picture
                        });
                        setRoute(embeddedRoute);
                    }
                }

            } catch (err: any) {
                console.error('Error fetching climb data:', err);
                setError('Failed to load climb data');
            } finally {
                setLoading(false);
            }
        };

        fetchClimbData();
    }, [currentUser, gymId, climbIndex]);

    // Auto-determine performance based on topped status and attempt number
    useEffect(() => {
        if (isTopped) {
            if (editAttempt === 1) {
                setEditPerformance(ClimbPerformance.ON_SIGHT);
            } else {
                setEditPerformance(ClimbPerformance.REDPOINT);
            }
        } else {
            setEditPerformance(ClimbPerformance.NOT_TOPPED);
        }
    }, [isTopped, editAttempt]);

    const handleEdit = () => {
        setEditMode(true);
    };

    const handleCancelEdit = () => {
        if (!climb) return;
        
        // Reset form to original values
        const currentIsTopped = climb.performance !== ClimbPerformance.NOT_TOPPED;
        setEditPerformance(climb.performance);
        setIsTopped(currentIsTopped);
        setEditHangs(climb.hangs || 0);
        setEditAttempt(climb.attempt || 1);
        setEditNotes(climb.notes || '');
        setEditStyle(climb.style || '');
        setEditReachedTop(climb.reachedTop !== undefined ? climb.reachedTop : true);
        setEditClimbDate(new Date(climb.date).toISOString().split('T')[0]);
        setEditMode(false);
    };

    const handleSaveEdit = async () => {
        if (!currentUser || !climb || climbIndex === undefined) return;

        try {
            setUpdating(true);
            const climbIdx = parseInt(climbIndex);

            // Convert date string to timestamp (midnight of the selected date)
            const dateTimestamp = new Date(editClimbDate).setHours(0, 0, 0, 0);

            const updatedData: Partial<ClimbRecord> = {
                performance: editPerformance,
                attempt: editAttempt,
                reachedTop: isTopped ? true : editReachedTop,
                date: dateTimestamp
            };

            // Only add hangs if not topped
            if (!isTopped) {
                updatedData.hangs = editHangs;
            }

            // Only add notes if not empty
            if (editNotes.trim()) {
                updatedData.notes = editNotes.trim();
            }

            // Only add style if not empty
            if (editStyle.trim()) {
                updatedData.style = editStyle.trim();
            }

            console.log('Updating climb with data:', {
                oldPerformance: climb.performance,
                newPerformance: editPerformance,
                routeId: climb.routeId,
                gymId: gymId
            });

            await updateClimb(currentUser.uid, climbIdx, updatedData);

            // Update local state
            const updatedClimb = { ...climb, ...updatedData };
            setClimb(updatedClimb);
            setEditMode(false);

            console.log('Climb updated successfully');

        } catch (err: any) {
            console.error('Error updating climb:', err);
            setError('Failed to update climb');
        } finally {
            setUpdating(false);
        }
    };

    // Helper function to handle topped selection
    const handleToppedSelection = () => {
        setIsTopped(true);
    };

    // Helper function to handle attempted selection
    const handleAttemptedSelection = () => {
        setIsTopped(false);
    };

    // Helper function to determine if hangs input should be shown
    const shouldShowHangs = () => {
        return !isTopped;
    };

    const handleDelete = async () => {
        if (!currentUser || climbIndex === undefined) return;

        try {
            setUpdating(true);
            const climbIdx = parseInt(climbIndex);
            
            console.log('Deleting climb:', {
                climbIndex: climbIdx,
                performance: climb?.performance,
                routeId: climb?.routeId,
                gymId: gymId
            });
            
            await deleteClimb(currentUser.uid, climbIdx);
            
            console.log('Climb deleted successfully');
            
            // Navigate back to gym detail with climbs tab
            navigate(`/gyms/${gymId}?tab=climbs`);

        } catch (err: any) {
            console.error('Error deleting climb:', err);
            setError('Failed to delete climb');
            setUpdating(false);
        }
    };

    const getPerformanceLabel = (performance: ClimbPerformance, hangs?: number): string => {
        switch (performance) {
            case ClimbPerformance.TOPPED: return 'Topped';
            case ClimbPerformance.FLASH: return 'Flash';
            case ClimbPerformance.ON_SIGHT: return 'On Sight';
            case ClimbPerformance.REDPOINT: return 'Topped';
            case ClimbPerformance.NOT_TOPPED: return hangs !== undefined ? `${hangs} ${hangs === 1 ? 'Hang' : 'Hangs'}` : 'Attempted';
            case ClimbPerformance.BLOCKS: return 'Blocks';
            default: return String(performance);
        }
    };

    const getColorHex = (colorName?: string): string => {
        if (!colorName) return '#cccccc';
        const color = AvailableColors.find(c => c.value === colorName);
        return color ? color.hexCode : '#cccccc';
    };

    const isColorDark = (hexColor: string): boolean => {
        if (!hexColor || hexColor.length < 7) return false;
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness < 128;
    };

    if (loading) {
        return (
            <div className="climb-detail-container">
                <PageHeader title="Climb Details" />
                <div className="loading-indicator">Loading climb data...</div>
            </div>
        );
    }

    if (error || !climb) {
        return (
            <div className="climb-detail-container">
                <PageHeader title="Climb Details" />
                <div className="error-message">{error || 'Climb not found'}</div>
            </div>
        );
    }

    const colorHex = route ? getColorHex(route.color) : '#cccccc';
    const isDark = isColorDark(colorHex);
    const gradeText = route && route.grade !== undefined ? getGradeAsString(route.grade, GradeSystem.FRENCH) : 'N/A';

    return (
        <div className="climb-detail-container">
            <PageHeader 
                title="Climb Details"
                showMenu={false}
                headerActions={[
                    <MyIconButton 
                        key="close" 
                        onClick={() => navigate(`/gyms/${gymId}?tab=climbs`)} 
                        ariaLabel="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </MyIconButton>
                ]}
            />

            <div className="climb-detail-content">
                {/* Route Information */}
                <div className="route-info-section">
                    <h2>Route Information</h2>
                    <div className="route-display">
                        <div className="grade-circle" style={{ 
                            backgroundColor: colorHex, 
                            color: isDark ? 'white' : 'black',
                            borderColor: colorHex === '#ffffff' ? '#dddddd' : 'transparent'
                        }}>
                            {gradeText}
                        </div>
                        <div className="route-meta">
                            {route?.location && <div>Location: {route.location}</div>}
                            {route?.rope && <div>Rope: {route.rope}</div>}
                            {route?.setterName && <div>Setter: {route.setterName}</div>}
                            {climb?.style && <div>Style: {climb.style}</div>}
                            {route?.status === 'archived' && (
                                <div style={{ fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
                                    (Archived Route)
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Climb Information */}
                <div className="climb-info-section">
                    <div className="section-header">
                        <h2>Climb Details</h2>
                        {!editMode && (
                            <MyIconButton onClick={handleEdit} ariaLabel="Edit climb">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width: '20px', height: '20px'}}>
                                    <path d="m18 2 4 4-14 14H4v-4L18 2Z"/>
                                    <path d="m14.5 5.5 4 4"/>
                                </svg>
                            </MyIconButton>
                        )}
                    </div>

                    {editMode ? (
                        <div className="edit-form">
                            <div className="form-group">
                                <label>How did you climb it?</label>
                                <div className="performance-chips">
                                    <button 
                                        type="button"
                                        className={`chip ${isTopped ? 'selected' : ''}`}
                                        onClick={handleToppedSelection}
                                    >
                                        Topped
                                    </button>
                                    <button 
                                        type="button"
                                        className={`chip ${!isTopped ? 'selected' : ''}`}
                                        onClick={handleAttemptedSelection}
                                    >
                                        Attempted
                                    </button>
                                </div>
                                
                                {isTopped && (
                                    <div className="performance-info">
                                        <p style={{ marginTop: '12px', color: '#666', fontSize: '0.9em' }}>
                                            {editAttempt === 1 
                                                ? "🎯 This will be recorded as an On Sight (was your first attempt)" 
                                                : "🔄 This will be recorded as a Redpoint (was attempt #" + editAttempt + ")"}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {shouldShowHangs() && (
                                <div className="form-group">
                                    <label htmlFor="hangs">Number of Hangs/Rests</label>
                                    <div className="number-picker">
                                        <button 
                                            type="button" 
                                            className="number-picker-btn" 
                                            onClick={() => setEditHangs(Math.max(1, editHangs - 1))}
                                            disabled={editHangs <= 1}
                                        >
                                            -
                                        </button>
                                        <div className="number-picker-value">{editHangs}</div>
                                        <button 
                                            type="button" 
                                            className="number-picker-btn"
                                            onClick={() => setEditHangs(editHangs + 1)}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!isTopped && (
                                <div className="form-group">
                                    <label htmlFor="reachedTop">Did you reach the top?</label>
                                    <div className="checkbox-group">
                                        <input
                                            id="reachedTop"
                                            type="checkbox"
                                            checked={editReachedTop}
                                            onChange={(e) => setEditReachedTop(e.target.checked)}
                                        />
                                        <label htmlFor="reachedTop" className="checkbox-label">Yes, I reached the top</label>
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="climbDate">Date of Climb</label>
                                <div className="date-picker-container">
                                    <input
                                        id="climbDate"
                                        type="date"
                                        value={editClimbDate}
                                        onChange={(e) => setEditClimbDate(e.target.value)}
                                        max={new Date().toISOString().split('T')[0]} // Can't select future dates
                                    />
                                    <div className="current-date-display">
                                        {new Date(editClimbDate).toLocaleDateString('en-US', { 
                                            weekday: 'short', 
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric' 
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="style">Style</label>
                                <input 
                                    type="text" 
                                    id="style"
                                    value={editStyle} 
                                    onChange={(e) => setEditStyle(e.target.value)}
                                    placeholder="e.g., Lead, Top Rope, Boulder"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="notes">Notes (optional)</label>
                                <textarea 
                                    id="notes"
                                    value={editNotes} 
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    placeholder="Add any comments about your climb..."
                                    rows={4}
                                />
                            </div>

                            <div className="form-actions">
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleSaveEdit}
                                    disabled={updating}
                                >
                                    {updating ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={handleCancelEdit}
                                    disabled={updating}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="climb-display">
                            <div className="climb-detail-item">
                                <span className="label">Date:</span>
                                <span className="value">{new Date(climb.date).toLocaleDateString()}</span>
                            </div>
                            <div className="climb-detail-item">
                                <span className="label">Performance:</span>
                                <span className="value performance">
                                    {(() => {
                                        // Show specific performance type instead of generic "Topped"
                                        if (climb.performance === ClimbPerformance.ON_SIGHT) {
                                            return <div className="performance-badge onsight">On Sight</div>;
                                        }
                                        if (climb.performance === ClimbPerformance.FLASH) {
                                            return <div className="performance-badge flash">Flash</div>;
                                        }
                                        if (climb.performance === ClimbPerformance.REDPOINT) {
                                            return <div className="performance-badge topped">Topped</div>;
                                        }
                                        if (climb.performance === ClimbPerformance.TOPPED) {
                                            return <div className="performance-badge topped">Topped</div>;
                                        }
                                        if ([ClimbPerformance.NOT_TOPPED, ClimbPerformance.BLOCKS].includes(climb.performance) && climb.hangs !== undefined) {
                                            return <div className="performance-badge not-topped">{climb.hangs} {climb.hangs === 1 ? 'Hang' : 'Hangs'}</div>;
                                        }
                                        // Fallback for other performance types
                                        return <div className="performance-badge">{getPerformanceLabel(climb.performance, climb.hangs)}</div>;
                                    })()}
                                </span>
                            </div>
                            {climb.attempt && climb.attempt > 1 && (
                                <div className="climb-detail-item">
                                    <span className="label">Attempt:</span>
                                    <span className="value">#{climb.attempt}</span>
                                </div>
                            )}
                            {climb.notes && (
                                <div className="climb-detail-item">
                                    <span className="label">Notes:</span>
                                    <span className="value notes">{climb.notes}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Delete Section */}
                {!editMode && (
                    <div className="delete-section">
                        {!showDeleteConfirm ? (
                            <button 
                                className="btn btn-danger" 
                                onClick={() => setShowDeleteConfirm(true)}
                            >
                                Delete Climb
                            </button>
                        ) : (
                            <div className="delete-confirm">
                                <p>Are you sure you want to delete this climb?</p>
                                <div className="delete-actions">
                                    <button 
                                        className="btn btn-danger" 
                                        onClick={handleDelete}
                                        disabled={updating}
                                    >
                                        {updating ? 'Deleting...' : 'Yes, Delete'}
                                    </button>
                                    <button 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowDeleteConfirm(false)}
                                        disabled={updating}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClimbDetail;
